/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import { Typography, Button } from '@mui/material';
import { InputLabel, Textarea, Select, RadioGroup, AvailabilitySelector } from 'components/elements';
import { useFetchTimeZones, useFetchConfiguration, useFetchLocationPreference } from 'hooks';
import { validators, timeZone } from 'utils';
import Styles from './styles';

const useStyles = makeStyles(Styles);

function AppointmentDetailsForm({ onSubmit, formId, submitRef, ...restProps }) {
  const classes = useStyles();
  const { t } = useTranslation('labels');
  const { isEnabledField, isRequiredField } = useFetchConfiguration();
  const { data: timezones } = useFetchTimeZones();
  const { data: locationPrefOptions } = useFetchLocationPreference();

  const { handleSubmit, control, getValues } = useForm({
    mode: 'onSubmit',
    defaultValues: restProps,
  });

  const onSubmitForm = (goBack) => {
    const values = getValues();
    onSubmit(values, goBack);
  };

  return (
    <div className={classes.clientInfoFormWrapper}>
      {!restProps.isServiceRequest && <h1 className={classes.formHeader}>{t('appointment_details')}</h1>}
      <form id={formId} className={classes.appointmentDetailsForm} onSubmit={handleSubmit(() => onSubmitForm(false))}>
        {!restProps.isServiceRequest && isEnabledField('timezone') && (
          <div className={classes.timezone}>
            <Controller
              name="timezone"
              control={control}
              rules={{
                required: {
                  value: isRequiredField('timezone'),
                  message: t('please_fill_in_this_field'),
                },
              }}
              render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                <Select
                  options={timezones}
                  label={<InputLabel text={t('timezone')} required={isRequiredField('timezone')} />}
                  helperText={error ? error.message : null}
                  error={!!error}
                  {...restField}
                />
              )}
            />
          </div>
        )}
        {isEnabledField('availabilitySelector') && (
          <div className={classes.availabilitySelector}>
            {(isEnabledField('morningTiming') || isEnabledField('afternoonTiming') || isEnabledField('eveningTiming')) && (
              <Controller
                name="availabilityPreferences"
                control={control}
                rules={{
                  validate: (value) => {
                    if (!validators.availabilityValidator(value, isRequiredField('availabilitySelector'))) {
                      return t('please_fill_in_this_field');
                    }
                    return null;
                  },
                }}
                render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                  <AvailabilitySelector
                    isMorningEnabled={isEnabledField('morningTiming')}
                    isAfternoonEnabled={isEnabledField('afternoonTiming')}
                    isEveningEnabled={isEnabledField('eveningTiming')}
                    label={
                      <>
                        <InputLabel text={t('please_select_your_availability')} required={isRequiredField('availabilitySelector')} />
                        <Typography className={classes.availabilityLabel} variant="body2" component="p">
                          {t('please_indicate_all_times_during_which_you_are_available_for_scheduling')}
                        </Typography>
                      </>
                    }
                    helperText={error ? error.message : null}
                    error={!!error}
                    {...restField}
                  />
                )}
              />
            )}
            <Controller
              name="availabilityPreferencesDetail"
              control={control}
              render={({ field: { ref, ...restField } }) => (
                <Textarea label={<InputLabel text={t('please_feel_free_to_provide_any_additional_details_regarding_your_appointment_preference')} />} limit={500} {...restField} />
              )}
            />
          </div>
        )}
        {isEnabledField('locationPreference') && (
          <div className={classes.locationPreference}>
            <Controller
              name="locationPreference"
              control={control}
              rules={{
                required: {
                  value: isRequiredField('locationPreference'),
                  message: t('please_fill_in_this_field'),
                },
              }}
              render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                <RadioGroup
                  options={locationPrefOptions}
                  error={!!error}
                  helperText={error ? error.message : null}
                  {...restField}
                  label={<InputLabel text={t('are_you_open_to_meeting_with_your_provider_in_person_or_online')} required={isRequiredField('locationPreference')} />}
                  required={isRequiredField('locationPreference')}
                />
              )}
            />
          </div>
        )}
        {restProps.isServiceRequest && (
          <div className={classes.controlButton}>
            <Button onClick={() => onSubmitForm(true)} variant="outlined" color="primary" size="large" className={classes.backButton} ref={submitRef}>
              {t('back')}
            </Button>
            <Button type="submit" variant="contained" color="primary" size="large" className={classes.serviceBackButton}>
              {t('next')}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}

AppointmentDetailsForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  submitRef: PropTypes.objectOf(PropTypes.any).isRequired,
  formId: PropTypes.string.isRequired,
  timezone: PropTypes.string,
  availabilityPreferences: PropTypes.shape({
    morning: PropTypes.arrayOf(PropTypes.number),
    afternoon: PropTypes.arrayOf(PropTypes.number),
    evening: PropTypes.arrayOf(PropTypes.number),
  }),
  availabilityPreferencesDetail: PropTypes.string,
  locationPreference: PropTypes.string,
};

AppointmentDetailsForm.defaultProps = {
  timezone: timeZone.tryGuessTZ(),
  availabilityPreferences: {
    morning: [],
    afternoon: [],
    evening: [],
  },
  availabilityPreferencesDetail: '',
  locationPreference: '',
};

export default AppointmentDetailsForm;
